<script lang="ts">
	import {
		Alert,
		ButtonGroup,
		ButtonV2,
		InputV2,
		Text,
		toast,
		ToggleV2,
	} from '@Shelly/shelly-design-system/index';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import {
		celsius_to_fahrenheit,
		defaultTempFavPositions,
		isWallDisplay,
		isBluTRV,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar, valueOutOfRange } from 'src/helpers/utilities';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import ValveFavPositions from './ValveFavPositions.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';

	export let device: Device;

	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	// Check if this is a BLU TRV and if automatic temperature control is enabled
	const isTRV = isBluTRV(device.type);
	$: enableTrv = isTRV ? getTrvComponentByDevice(device)?.config?.['trv:0']?.enable : true;

	// ButtonGroup selection: 'temperature' or 'valve'
	let selectedView: 'temperature' | 'valve' = enableTrv ? 'temperature' : 'valve';

	// After first render, remember user's choice
	let hasRendered = false;
	$: if (!hasRendered && typeof enableTrv !== 'undefined') {
		hasRendered = true;
		selectedView = enableTrv ? 'temperature' : 'valve';
	}
	let prevUnit =
		device?.ht_ui?.temperature_unit ||
		settings?.thermostats?.[0]?.target_t?.units ||
		settings?.['thermostat:0']?.display_unit ||
		'C';
	$: unit =
		device?.ht_ui?.temperature_unit ||
		settings?.thermostats?.[0]?.target_t?.units ||
		settings?.['thermostat:0']?.display_unit ||
		'C';
	let minValue = 4;
	let maxValue = 30;
	if (isWallDisplay(device.type)) {
		minValue = 5;
		maxValue = 35;
	}
	$: min = unit === 'C' ? minValue : Math.round(celsius_to_fahrenheit(minValue));
	$: max = unit === 'C' ? maxValue : Math.round(celsius_to_fahrenheit(maxValue));
	$: temp_counter = device?.temp_counter || 0;
	let temp_fav_positions = JSON.parse(
		JSON.stringify(device?.temp_fav_positions || defaultTempFavPositions(prevUnit)),
	);

	$: if (prevUnit !== unit) {
		temp_fav_positions = JSON.parse(
			JSON.stringify(device?.temp_fav_positions || defaultTempFavPositions(unit)),
		);
		prevUnit = unit;
	}

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: saveDisabled = !!temp_fav_positions.find(
		(i) => valueOutOfRange(i.value, min, max) && i.active,
	);

	$: if (stickyBottomAction) {
		if ($showNewLayout && selectedView === 'temperature') {
			setStickyBottomAction({
				onClick: saveTempFavPosition,
				loading: false,
				disabled: saveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	const saveTempFavPosition = async () => {
		const res = await requestEditDevice({
			...device,
			...(isWallDisplay(device.type) && { id: real_id(device.id) }),
			temp_fav_positions,
			temp_counter: temp_counter + 1,
		});
		if (res?.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_success_fav_temp_positions', {
					default: 'Successfully saved favorite temperature positions',
				}),
			});
		}
	};
</script>

{#if isTRV}
	<ButtonGroup
		aria-label={$format('button_group_favorite_positions_type', {
			default: 'Select favorite positions type',
		})}
		class="mb-m"
		size="m"
		buttons={[
			{
				id: 'temperature',
				label: $format('button_temperature_favorites', {
					default: 'Temperature',
				}),
				icon: 'fas fa-temperature-three-quarters',
			},
			{
				id: 'valve',
				label: $format('button_valve_favorites', { default: 'Valve position' }),
				icon: 'fas fa-gauge',
			},
		]}
		selected={selectedView}
		on:click={(e) => (selectedView = e.detail.id)}
	/>
{/if}

{#if selectedView === 'temperature'}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('alert_info_heading_unit_temp_fav_positions', {
			default:
				'Your device currently measures in {unitName} (°{unitValue}), so the values must be between {min} and {max}.',
			values: {
				unitName:
					unit === 'C'
						? $format('label_celsius', { default: 'Celsius' })
						: $format('label_fahrenheit', { default: 'Fahrenheit' }),
				unitValue: unit,
				min,
				max,
			},
		})}
	/>
	{#each temp_fav_positions as pos (pos.mode)}
		<Text styleType="body-small" class="mb-xs">
			{$format(`temp_fav_position_${pos.mode}`, {
				default: capFirstChar(pos.mode),
			})}
		</Text>
		<div class="mb-s d-flex align-items-start">
			<InputV2
				aria-label={$format('input_set_favorite_temperature', {
					default: 'Input set favorite temperature',
				})}
				size="m"
				class="mr-s"
				type="number"
				{min}
				{max}
				bind:value={pos.value}
				disabled={!pos.active}
				error={valueOutOfRange(pos.value, min, max)
					? $format('min_max_error_description', {
							values: { min, max },
							default: 'Value must be between {min} and {max}.',
						})
					: ''}
				icon={getWorkingModeIcon(pos.mode)}
				id={`input-fav-temp-${pos.mode}`}
			/>
			<ToggleV2
				aria-label={$format('toggle_set_favorite_temperature', {
					default: 'Toggle set favorite temperature',
				})}
				size="m"
				checked={pos.active}
				on:click={() => {
					pos.active = !pos.active;
					temp_fav_positions = temp_fav_positions;
				}}
				id={`toggle-fav-temp-${pos.mode}`}
			/>
		</div>
	{/each}
	{#if !$showNewLayout}
		<ButtonV2
			disabled={saveDisabled}
			class="mt-s"
			size="m"
			width="full"
			on:click={saveTempFavPosition}
		>
			{$format('button_save')}
		</ButtonV2>
	{/if}
{:else if selectedView === 'valve'}
	<ValveFavPositions {device} />
{/if}
